import React from "react";
import CrudPage from "../common/CrudPage";

export const ServiceCategoriesPage = () => (
  <CrudPage schema={{
    title: "Service Categories", subtitle: "Service & Pricing", path: "/service-categories",
    columns: [
      { key: "name", label: "Name" },
      { key: "kind", label: "Kind", render: (r) => (
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${r.kind === "drop" ? "bg-[#F9E4B7] text-[#7A5A11]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
          {r.kind === "drop" ? "Drop" : "Home Service"}
        </span>
      )},
      { key: "description", label: "Description" },
      { key: "status", label: "Status" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "kind", label: "Kind", type: "select", default: "home_service",
        options: [{ value: "drop", label: "Drop (customer drops at center)" },
                   { value: "home_service", label: "Home Service (on-premises)" }] },
      { key: "description", label: "Description", type: "text" },
      { key: "status", label: "Status", type: "select", default: "active",
        options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
    ],
  }}/>
);

export const ServicesPage = () => (
  <CrudPage schema={{
    title: "Service Types", subtitle: "Service & Pricing", path: "/services",
    columns: [
      { key: "name", label: "Name" },
      { key: "type", label: "Type", render: (r) => (
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
          r.type === "emergency" ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#D4E0D1] text-[#1A3317]"
        }`}>{r.type}</span>
      )},
      { key: "category_id", label: "Category" },
      { key: "base_charge", label: "Base ₹", render: (r) => <span className="font-mono">₹{Number(r.base_charge).toFixed(2)}</span> },
      { key: "status", label: "Status" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category_id", label: "Service category",
        type: "remote_select", path: "/service-categories",
        labelFn: (o) => `${o.name} (${o.kind === "drop" ? "Drop" : "Home service"})`,
        placeholder: "— select category —", required: true },
      { key: "type", label: "Type", type: "select", default: "regular",
        options: [{ value: "regular", label: "Regular" }, { value: "emergency", label: "Emergency" }, { value: "drop", label: "Drop" }],
        helper: "For Home Service: Regular or Emergency. For Drop: use Drop." },
      { key: "base_charge", label: "Base charge ₹", type: "number", required: true },
      { key: "status", label: "Status", type: "select", default: "active",
        options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
    ],
  }}/>
);

export const WasteTypesPage = () => (
  <CrudPage schema={{
    title: "Waste Types", subtitle: "Service & Pricing", path: "/waste-types",
    csv: {
      path: "/waste-types",
      templateFile: "waste_types_template.csv",
      helpText: "Columns: category, subcategory, transaction_type (PAY/CHARGE), waste_name, customer_rate, vendor_rate, type (recyclable/disposal/hazardous/medical/industrial/organic), profit_per_kg (optional override — auto-computed if blank), field_purpose, co2_factor. Rows matched by (category + subcategory + waste_name) are updated; others are created.",
    },
    columns: [
      { key: "waste_name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "type", label: "Type", render: (r) => (
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
          r.type === "hazardous" ? "bg-[#F4C7B9] text-[#7A2E18]"
          : r.type === "medical" ? "bg-[#E8CFD8] text-[#7A1F3A]"
          : r.type === "industrial" ? "bg-[#D6DDE6] text-[#1E3A5F]"
          : r.type === "organic" ? "bg-[#D4E0D1] text-[#1A3317]"
          : r.type === "disposal" ? "bg-[#F9E4B7] text-[#7A5A11]"
          : "bg-[#D4E0D1] text-[#1A3317]"
        }`}>{r.type || "—"}</span>
      )},
      { key: "transaction_type", label: "Txn", render: (r) => (
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded font-medium ${
          r.transaction_type === "PAY" ? "bg-[#C25D38]/15 text-[#7A2E18]" : "bg-[#2D5A27]/15 text-[#1A3317]"
        }`}>{r.transaction_type || "CHARGE"}</span>
      )},
      { key: "customer_rate", label: "Customer ₹/kg", render: (r) => <span className="font-mono">₹{Number(r.customer_rate ?? r.rate_per_kg ?? 0).toFixed(2)}</span> },
      { key: "vendor_rate", label: "Vendor ₹/kg", render: (r) => <span className="font-mono">₹{Number(r.vendor_rate ?? 0).toFixed(2)}</span> },
      { key: "profit_per_kg", label: "Profit ₹/kg", render: (r) => {
        const p = Number(r.profit_per_kg ?? 0);
        return <span className={`font-mono font-semibold ${p >= 0 ? "text-[#2D5A27]" : "text-[#C25D38]"}`}>₹{p.toFixed(2)}</span>;
      }},
      { key: "co2_factor", label: "CO₂", render: (r) => <span className="font-mono text-[#2D5A27]">{Number(r.co2_factor ?? 0).toFixed(2)}</span> },
    ],
    fields: [
      { key: "waste_name", label: "Waste name", type: "text", required: true },
      { key: "category", label: "Category", type: "text", required: true },
      { key: "subcategory", label: "Subcategory", type: "text" },
      { key: "type", label: "Type", type: "select", default: "recyclable",
        options: [
          { value: "recyclable", label: "Recyclable (resale value)" },
          { value: "disposal", label: "Disposal (landfill / incinerator)" },
          { value: "hazardous", label: "Hazardous" },
          { value: "medical", label: "Medical (bio-medical)" },
          { value: "industrial", label: "Industrial" },
          { value: "organic", label: "Organic (compostable)" },
        ] },
      { key: "transaction_type", label: "Transaction", type: "select", default: "CHARGE",
        options: [
          { value: "CHARGE", label: "CHARGE — customer pays us (e.g. diapers)" },
          { value: "PAY", label: "PAY — we pay customer (e.g. paper, plastic)" },
        ],
        helper: (f) => f.transaction_type === "PAY"
          ? "Customer brings this waste; we buy @ customer rate and resell @ vendor rate. Profit = vendor − customer."
          : "Customer pays us for collection; we pay disposal partner. Profit = customer − vendor." },
      { key: "customer_rate", label: "Customer rate ₹/kg", type: "number", required: true,
        helper: (f) => f.transaction_type === "PAY" ? "Rate we pay the customer for their waste" : "Rate we charge the customer for collection" },
      { key: "vendor_rate", label: "Vendor rate ₹/kg", type: "number", required: true,
        helper: (f) => f.transaction_type === "PAY" ? "Rate we receive when we sell this to a vendor" : "Rate we pay the disposal partner" },
      { key: "profit_override", label: "Override auto-computed profit", type: "checkbox", default: false, uiOnly: true },
      { key: "profit_per_kg", label: "Profit ₹/kg (manual override)", type: "number",
        helper: (f) => {
          const cr = Number(f.customer_rate || 0);
          const vr = Number(f.vendor_rate || 0);
          const auto = f.transaction_type === "PAY" ? vr - cr : cr - vr;
          return `Auto would be ₹${auto.toFixed(2)} — ${f.transaction_type === "PAY" ? "vendor − customer" : "customer − vendor"}. This override is saved as-is.`;
        },
        showIf: (f) => !!f.profit_override,
        clearWhen: (f) => !f.profit_override },
      { key: "field_purpose", label: "Field purpose / downstream use", type: "text",
        helper: "Where does this waste go? e.g. 'Resold to PET recycler', 'Certified e-waste handler'" },
      { key: "co2_factor", label: "CO₂ factor (kg saved per kg collected)", type: "number" },
    ],
  }}/>
);

export const ChargesPage = () => (
  <CrudPage schema={{
    title: "Service Priority",
    subtitle: "Service & Pricing · syncs with service request priority",
    path: "/charges",
    columns: [
      { key: "priority", label: "Priority", render: (r) => {
        const p = r.priority || "—";
        const cls = p === "URGENT" ? "bg-[#F4C7B9] text-[#7A2E18]"
          : p === "HIGH" ? "bg-[#F9E4B7] text-[#7A5A11]"
          : p === "NORMAL" ? "bg-[#D4E0D1] text-[#1A3317]"
          : "bg-[#E5E2D9] text-[#5C5A55]";
        return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${cls}`}>{p}</span>;
      }},
      { key: "type", label: "Label" },
      { key: "amount", label: "Surcharge ₹", render: (r) => <span className="font-mono">₹{Number(r.amount).toFixed(2)}</span> },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "priority", label: "Priority level *", type: "select", required: true,
        default: "NORMAL",
        options: [
          { value: "NORMAL",  label: "Normal — routine collection, no surcharge" },
          { value: "HIGH",    label: "High — priority queue, moderate surcharge" },
          { value: "URGENT",  label: "Urgent — same/next day, max surcharge" },
        ],
        helper: "This value drives which surcharge is auto-applied on a Service Request with the matching priority." },
      { key: "type", label: "Internal label / code *", type: "text", required: true,
        helper: "Short identifier — e.g. 'emergency_visit', 'instant_pickup'. Shown to admins only." },
      { key: "amount", label: "Surcharge ₹ *", type: "number", required: true },
      { key: "description", label: "Customer-facing description", type: "text",
        helper: "Displayed to the customer on the priority chip in the SR form." },
    ],
  }}/>
);

const gstAmount = (price, pct, inclusive) => {
  const p = Number(price) || 0;
  const g = Number(pct) || 0;
  if (!p || !g) return 0;
  return inclusive ? +(p - p / (1 + g / 100)).toFixed(2)
                   : +(p * g / 100).toFixed(2);
};

export const ProductsPage = () => (
  <CrudPage schema={{
    title: "Products", subtitle: "Service & Pricing", path: "/products",
    columns: [
      { key: "name", label: "Name" },
      { key: "price", label: "Price", render: (r) => <span className="font-mono">₹{Number(r.price).toFixed(2)}</span> },
      { key: "gst", label: "GST", render: (r) => r.gst_enabled
        ? <span className="text-xs font-mono">{Number(r.gst_percent).toFixed(1)}% · {r.gst_inclusive ? "incl" : "excl"}</span>
        : <span className="text-xs text-[#8A8780]">—</span> },
      { key: "stock", label: "Stock" },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "price", label: "Price ₹", type: "number", required: true },
      { key: "stock", label: "Stock", type: "number" },
      { key: "gst_enabled", label: "GST applicable", type: "checkbox", default: false },
      { key: "gst_percent", label: "GST %", type: "number", default: 0,
        showIf: (f) => !!f.gst_enabled },
      { key: "gst_inclusive", label: "Price is GST inclusive", type: "checkbox", default: true,
        showIf: (f) => !!f.gst_enabled,
        helper: (f) => {
          const amt = gstAmount(f.price, f.gst_percent, f.gst_inclusive);
          return `GST amount: ₹${amt.toFixed(2)} · ${f.gst_inclusive ? "extracted from price" : "added on top → total ₹" + (Number(f.price || 0) + amt).toFixed(2)}`;
        } },
    ],
  }}/>
);
